import { useState, type ReactElement } from 'react';
import { Search } from 'lucide-react';
import {
  catalogMealTypeLabel,
  catalogTagLabel,
  pilaBadgeClass,
  pilaLabel,
  useRecipeCatalog,
  type CatalogMealType,
  type PilaLevel,
  type RecipeCatalogItem,
} from '@/lib/nutrition';
import { Modal } from './RecipeCatalogView';

/**
 * Modal "Cambiar receta" — listado del catálogo pre-filtrado por la pila +
 * tipo de comida del meal que el usuario quiere cambiar.
 *
 * Decisiones:
 *  - Pre-filtrado server-side (mediante `useRecipeCatalog({pila, mealType})`).
 *    Cuando `currentRecipeId` apunta a una receta de usuario (no template), el
 *    backend igualmente devolverá sólo templates compatibles; el usuario
 *    revisa y aplica.
 *  - Las cards son `<label><input type="radio" />` para mantener semántica
 *    nativa (teclado: arrow keys cambian la selección dentro del role="radiogroup").
 *  - "Aplicar" llama `onSelect(recipeId, servings)`; la persistencia la hace el
 *    caller (típicamente `WeekMealPlanView` con un PATCH al meal item).
 *  - "Crear receta nueva" queda disabled en M2.36 (sólo placeholder UX para
 *    indicar al usuario que la función existe); cuando se implemente, abrirá
 *    el form de generación con campos pre-rellenados.
 */
export function ChangeRecipeModal({
  currentRecipeId,
  pilaLevel,
  mealType,
  onClose,
  onSelect,
}: {
  currentRecipeId: number | string | null;
  pilaLevel: PilaLevel | null;
  mealType: CatalogMealType;
  onClose: () => void;
  onSelect: (recipeId: number | string, servings: number) => void;
}): ReactElement {
  const [q, setQ] = useState('');
  // El radio selecciona por id (string para mantenerlo serializable; los ids
  // numéricos también funcionan: el <input value> normaliza a string).
  const [selectedId, setSelectedId] = useState<string | null>(
    currentRecipeId !== null ? String(currentRecipeId) : null,
  );
  const [servings, setServings] = useState<number>(1);

  // Si el meal tenía pila null (snack/midmorning) consultamos sin filtro pila —
  // pasamos 'all' al endpoint, pero filtramos client-side a los que pueden
  // servir como midmorning/snack.
  const query = useRecipeCatalog({
    pila: pilaLevel === null ? 'all' : pilaLevel,
    mealType,
    q,
  });

  const recipes = query.data ?? [];
  const selected = recipes.find((r) => String(r.id) === selectedId) ?? null;

  function handleApply() {
    if (!selected) return;
    // `servings` >0 enforce.
    const s = servings > 0 ? servings : 1;
    onSelect(selected.id, s);
  }

  return (
    <Modal title="Cambiar receta" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-xs text-slate-600">
          Mostrando recetas compatibles con{' '}
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${pilaBadgeClass(pilaLevel)}`}
          >
            {pilaLabel(pilaLevel)}
          </span>{' '}
          ·{' '}
          <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-800 ring-1 ring-teal-200">
            {catalogMealTypeLabel(mealType)}
          </span>
        </p>

        {/* Search */}
        <div>
          <label
            htmlFor="change-recipe-search"
            className="block text-xs font-semibold uppercase tracking-wide text-slate-600"
          >
            Buscar por título
          </label>
          <div className="relative mt-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              id="change-recipe-search"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar recetas…"
              className="block w-full rounded-md border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
            />
          </div>
        </div>

        {query.isLoading && (
          <p role="status" aria-live="polite" className="text-slate-700">
            Cargando recetas…
          </p>
        )}

        {query.isError && (
          <p
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            No se pudo cargar el catálogo. {query.error?.message ?? ''}
          </p>
        )}

        {query.data && recipes.length === 0 && (
          <p className="rounded-md border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-sm text-slate-600">
            No hay recetas que encajen con este meal.
          </p>
        )}

        {recipes.length > 0 && (
          <fieldset
            className="max-h-[40vh] space-y-2 overflow-y-auto"
            aria-label="Recetas disponibles para reemplazar"
          >
            <legend className="sr-only">Recetas disponibles</legend>
            <div role="radiogroup" className="space-y-2">
              {recipes.map((r) => (
                <RecipeChoice
                  key={String(r.id)}
                  recipe={r}
                  selected={String(r.id) === selectedId}
                  onSelect={() => setSelectedId(String(r.id))}
                />
              ))}
            </div>
          </fieldset>
        )}

        {/* Servings */}
        <div className="flex items-end gap-3">
          <div className="w-32">
            <label
              htmlFor="change-recipe-servings"
              className="block text-xs font-semibold uppercase tracking-wide text-slate-600"
            >
              Raciones
            </label>
            <input
              id="change-recipe-servings"
              type="number"
              min={1}
              max={20}
              step={1}
              value={servings}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v > 0) setServings(Math.round(v));
              }}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
            />
          </div>
          {selected && (
            <p className="pb-2 text-xs text-slate-600">
              {selected.macros.kcal * servings} kcal · P{' '}
              {selected.macros.protG * servings}g · C{' '}
              {selected.macros.choG * servings}g · G{' '}
              {selected.macros.fatG * servings}g
            </p>
          )}
        </div>
      </div>

      <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
        <button
          type="button"
          disabled
          aria-disabled="true"
          title="Próximamente"
          className="cursor-not-allowed rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-400"
        >
          Crear receta nueva (próximamente)
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!selected}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            Aplicar
          </button>
        </div>
      </footer>
    </Modal>
  );
}

function RecipeChoice({
  recipe,
  selected,
  onSelect,
}: {
  recipe: RecipeCatalogItem;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition focus-within:ring-2 focus-within:ring-teal-600 focus-within:ring-offset-1 ${
        selected
          ? 'border-teal-600 bg-teal-50/50'
          : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
    >
      <input
        type="radio"
        name="change-recipe-choice"
        value={String(recipe.id)}
        checked={selected}
        onChange={onSelect}
        className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-600"
      />
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900">{recipe.title}</p>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${pilaBadgeClass(recipe.pilaLevel)}`}
          >
            {pilaLabel(recipe.pilaLevel)}
          </span>
          <span>
            {recipe.macros.kcal} kcal · P {recipe.macros.protG}g · C{' '}
            {recipe.macros.choG}g
          </span>
          {recipe.prepMin + recipe.cookMin > 0 && (
            <span>· {recipe.prepMin + recipe.cookMin} min</span>
          )}
        </p>
        {recipe.tags.length > 0 && (
          <ul className="mt-1 flex flex-wrap gap-1">
            {recipe.tags.slice(0, 3).map((t) => (
              <li
                key={`choice-${recipe.id}-${t}`}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 ring-1 ring-slate-200"
              >
                {catalogTagLabel(t)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </label>
  );
}
